import { BidGroup, BidTabsData, InternalEstimateMarkup } from "../interfaces/BidTabs";
import { fetchDataStrict } from "../clients/api";
import { handleError } from "../errors";
import { Result } from "./types";
import { CacheHandle, clearTogether, defineCachedResource } from "./helpers";

type BidGroupsPayload = { bidGroups: BidGroup[] };

// Populated bid-group query (bid_package, trade_estimator, updated_by) — the payload the cached
// fetcher serves. The stale check asks for one row and the count instead (getContractingServerState).
const fetchBidGroupsPopulatedUncached = async (projectId: string): Promise<Result<BidGroupsPayload>> => {
  try {
    const params: Record<string, string | number | string[]> = {
      projectId,
      search: ["qual;NULL", "AND", "frontend;NULL", "AND", `project;${projectId}`],
      populate: ["bid_package", "trade_estimator", "updated_by"],
    };
    const response = await fetchDataStrict({ table: "bid_groups", params });
    const bidGroups: BidGroup[] = response?.data || [];

    // Sort by bid package code if any groups exist
    if (bidGroups.length) {
      bidGroups.sort((a, b) => a.bid_package.code.localeCompare(b.bid_package.code));
    }

    return { success: true, data: { bidGroups } };
  } catch (error) {
    const err = handleError(error, { table: "bid_groups", context: "fetchBidGroupsPopulatedUncached" });
    return { success: false, error: err };
  }
};

const bidGroups = defineCachedResource<string, BidGroupsPayload>({
  scopeKey: (projectId) => projectId,
  fetch: fetchBidGroupsPopulatedUncached,
});

export const watchContractingForClear = bidGroups.watchForClear;

/**
 * Cached: memoized per projectId. Invalidate via clearContractingCaches().
 */
const fetchBidGroupsByProjectId = bidGroups.get;

// TODO: When the BidTabs card is built, follow the pattern in EstimateCardContainer:
// 1. Always call fetchBidGroupsByProjectId / fetchScopeSheetDataByProjectId to get data
//    (populates the in-memory cache)
// 2. Then, if hasDataSheets is true, call a separate table-write function to populate the Excel table
// The fetch and the table write should be separated — the API function owns the cache; the card owns the conditional write.

const fetchScopeSheetDataByProjectIdUncached = async (projectId: string): Promise<Result<BidTabsData>> => {
  try {
    const response = await fetchDataStrict({
      table: "data/l/EXPORT_INTERNAL_ESTIMATE_BY_PROJECT",
      params: { activeTab: "estimate", projectId },
    });
    const data = response?.data?.load as BidTabsData;
    return { success: true, data };
  } catch (error) {
    const err = handleError(error, {
      table: "data/l/EXPORT_INTERNAL_ESTIMATE_BY_PROJECT",
      context: "fetchScopeSheetDataByProjectIdUncached",
    });
    return { success: false, error: err };
  }
};

const scopeSheet = defineCachedResource<string, BidTabsData>({
  scopeKey: (projectId) => projectId,
  fetch: fetchScopeSheetDataByProjectIdUncached,
});

/**
 * Fetch scope-sheet data fresh and swap it into the caches on success; on failure the existing
 * cached entry is left intact as a fallback.
 */
export const refreshScopeSheetCache = scopeSheet.refresh;

/**
 * Cached: memoized per projectId. Invalidate via clearContractingCaches().
 */
const fetchScopeSheetDataByProjectId = scopeSheet.get;

const fetchInternalEstimateMarkupsByProjectIdUncached = async (
  projectId: string
): Promise<Result<InternalEstimateMarkup[]>> => {
  try {
    const response = await fetchDataStrict({
      table: "internal_estimate_markups",
      params: { search: `internal_estimate_markups.project;${projectId}` },
    });
    const markups = response?.data || [];
    return { success: true, data: markups };
  } catch (error) {
    const err = handleError(error, {
      table: "internal_estimate_markups",
      context: "fetchInternalEstimateMarkupsByProjectIdUncached",
    });
    return { success: false, error: err };
  }
};

const internalMarkups = defineCachedResource<string, InternalEstimateMarkup[]>({
  scopeKey: (projectId) => projectId,
  fetch: fetchInternalEstimateMarkupsByProjectIdUncached,
});

/** Everything contracting caches, for clearing or saving it as one. */
export const contractingCaches: CacheHandle[] = [bidGroups, scopeSheet, internalMarkups];

export const clearContractingCaches = (): void => clearTogether(contractingCaches);

export const stageBidGroups = bidGroups.stage;

export const stageScopeSheet = scopeSheet.stage;

export const stageInternalMarkups = internalMarkups.stage;

/**
 * Cached: memoized per projectId. Invalidate via clearContractingCaches().
 */
const fetchInternalEstimateMarkupsByProjectId = internalMarkups.get;

export {
  fetchBidGroupsByProjectId,
  fetchBidGroupsPopulatedUncached,
  fetchScopeSheetDataByProjectId,
  fetchScopeSheetDataByProjectIdUncached,
  fetchInternalEstimateMarkupsByProjectId,
  fetchInternalEstimateMarkupsByProjectIdUncached,
};
